package gr.tech.meetinggenie.styles

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import gr.tech.meetinggenie.util.CONSTANTS.FONT_FAMILY2
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent

/** Sidebar width in pixels when fully expanded. */
const val SIDEBAR_WIDTH_PX = 220

/** Sidebar width in pixels when collapsed (toggle-button strip only). */
const val SIDEBAR_COLLAPSED_PX = 44

// ─── Top Navigation Bar ────────────────────────────────────────────────────

/** Full-width top navigation bar (meetings list page). */
val TopNavStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.White)
            .borderBottom(1.px, LineStyle.Solid, Colors.LightGray)
            .padding(leftRight = 24.px, topBottom = 12.px)
    }
}

/** Brand title inside the top navbar — hidden on mobile, visible on MD+. */
val NavbarBrandTitleStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.None)
            .fontFamily(FONT_FAMILY2)
            .margin(0.px)
    }
    Breakpoint.MD {
        Modifier.display(DisplayStyle.InlineBlock)
    }
}

/** "Hello, name!" greeting text in the top navbar. */
val NavbarGreetingStyle = CssStyle {
    base {
        Modifier
            .margin(right = 12.px)
    }
}

// ─── Sidebar ───────────────────────────────────────────────────────────────

/**
 * Visual chrome for the left sidebar panel.
 * Width and padding are applied inline and driven by collapse state.
 * [Transition.all] ensures width and padding animate smoothly.
 * [WhiteSpace.NoWrap] prevents text/buttons from reflowing during the animation.
 */
val SidebarStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.White)
            .borderRight(1.px, LineStyle.Solid, Colors.LightGray)
            .overflow(Overflow.Hidden)
            .minWidth(0.px)   // allow collapsing below padding size
            .flexShrink(0)
            .whiteSpace(WhiteSpace.NoWrap)
            .transition(Transition.all(250.ms))
    }
}

/** Horizontal rule divider inside the sidebar. */
val SidebarDividerStyle = CssStyle {
    base {
        Modifier
            .borderTop(1.px, LineStyle.Solid, Colors.LightGray)
            .margin(topBottom = 12.px)
            .fillMaxWidth()
    }
}

/** H2 branding title inside the left sidebar. */
val SidebarTitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .color(Colors.Black)
            .fontSize(1.1.cssRem)
            .margin(0.px)
    }
}

/** Sidebar collapse/expand icon button (chevron or hamburger). */
val SidebarToggleStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .width(30.px)
            .height(30.px)
            .borderRadius(6.px)
            .cursor(Cursor.Pointer)
            .color(Colors.Gray)
            .backgroundColor(Colors.Transparent)
            .noBorder()
            .flexShrink(0)
    }
    hover {
        Modifier
            .color(Colors.Black)
            .backgroundColor(Theme.LightGray.rgb)
            .transition(Transition.all(100.ms))
    }
}

/**
 * Semi-transparent backdrop rendered behind the sidebar on mobile when open.
 * Clicking it closes the sidebar.
 */
val SidebarMobileBackdropStyle = CssStyle {
    base {
        Modifier
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .width(100.percent)
            .height(100.percent)
            .backgroundColor(rgba(0, 0, 0, 0.35))
            .zIndex(89)
            .cursor(Cursor.Pointer)
    }
}
